import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getFetchErrorMessage } from '../services/apiService';

interface Subscription {
  id: string;
  serviceName: string;
  provider: string;
  description?: string;
  monthlyPrice: number;
  billingFrequency: string;
  nextBillingDate?: string;
  isActive: boolean;
  autoRenew: boolean;
  notes?: string;
  createdAt: string;
  lastCheckedAt?: string;
}

interface SubscriptionsContentProps {
  refreshTrigger?: number;
  addTrigger?: number;
  onAddClick?: () => void;
}

const SubscriptionsContent: React.FC<SubscriptionsContentProps> = ({
  refreshTrigger,
  addTrigger,
  onAddClick,
}) => {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<Partial<Subscription>>({
    serviceName: '',
    provider: 'Railway',
    description: '',
    monthlyPrice: 0,
    billingFrequency: 'Monthly',
    nextBillingDate: '',
    isActive: true,
    autoRenew: true,
    notes: '',
  });

  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchSubscriptions();
  }, [refreshTrigger]);

  useEffect(() => {
    if (addTrigger && addTrigger > 0) {
      handleAddClick();
    }
  }, [addTrigger]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/subscriptions`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data);
      } else {
        setError(await getFetchErrorMessage(response, 'Nem sikerült betölteni az előfizetéseket.'));
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      setError(error instanceof Error ? error.message : 'Nem sikerült betölteni az előfizetéseket.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({
      serviceName: '',
      provider: 'Railway',
      description: '',
      monthlyPrice: 0,
      billingFrequency: 'Monthly',
      nextBillingDate: '',
      isActive: true,
      autoRenew: true,
      notes: '',
    });
    setAddDialogOpen(true);
    if (onAddClick) onAddClick();
  };

  const handleEditClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setFormData(subscription);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setDeleteDialogOpen(true);
  };

  const handleAddSubmit = async () => {
    try {
      const response = await fetch(`${apiUrl}/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSuccessMessage('Előfizetés sikeresen hozzáadva');
        setAddDialogOpen(false);
        fetchSubscriptions();
      } else {
        setError(await getFetchErrorMessage(response, 'Hiba történt az előfizetés hozzáadása során.'));
      }
    } catch (error) {
      console.error('Error adding subscription:', error);
      setError('Hiba történt az előfizetés hozzáadása során');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${apiUrl}/subscriptions/${selectedSubscription.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...formData, id: selectedSubscription.id }),
      });

      if (response.ok) {
        setSuccessMessage('Előfizetés sikeresen frissítve');
        setEditDialogOpen(false);
        fetchSubscriptions();
      } else {
        setError(await getFetchErrorMessage(response, 'Hiba történt az előfizetés frissítése során.'));
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      setError('Hiba történt az előfizetés frissítése során');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${apiUrl}/subscriptions/${selectedSubscription.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccessMessage('Előfizetés sikeresen törölve');
        setDeleteDialogOpen(false);
        fetchSubscriptions();
      } else {
        setError(await getFetchErrorMessage(response, 'Hiba történt az előfizetés törlése során.'));
      }
    } catch (error) {
      console.error('Error deleting subscription:', error);
      setError('Hiba történt az előfizetés törlése során');
    }
  };

  const getDaysUntilBilling = (nextBillingDate?: string) => {
    if (!nextBillingDate) return null;
    const today = new Date();
    const billingDate = new Date(nextBillingDate);
    const diffTime = billingDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getBillingChipColor = (daysUntil: number | null) => {
    if (daysUntil === null) return 'default';
    if (daysUntil < 0) return 'error';
    if (daysUntil <= 7) return 'warning';
    if (daysUntil <= 30) return 'info';
    return 'success';
  };

  const renderDialogForm = () => (
    <>
      <TextField
        fullWidth
        margin="normal"
        label="Szolgáltatás neve"
        value={formData.serviceName || ''}
        onChange={(e) => setFormData({ ...formData, serviceName: e.target.value })}
        required
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Szolgáltató</InputLabel>
        <Select
          value={formData.provider || 'Railway'}
          onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
          label="Szolgáltató"
        >
          <MenuItem value="Railway">Railway</MenuItem>
          <MenuItem value="Cloudflare">Cloudflare</MenuItem>
          <MenuItem value="Postmark">Postmark</MenuItem>
          <MenuItem value="Other">Egyéb</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        margin="normal"
        label="Leírás"
        value={formData.description || ''}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        multiline
        rows={2}
      />
      <TextField
        fullWidth
        margin="normal"
        label="Havi költség (USD)"
        type="number"
        value={formData.monthlyPrice || 0}
        onChange={(e) => setFormData({ ...formData, monthlyPrice: parseFloat(e.target.value) })}
        required
      />
      <FormControl fullWidth margin="normal">
        <InputLabel>Fizetési gyakoriság</InputLabel>
        <Select
          value={formData.billingFrequency || 'Monthly'}
          onChange={(e) => setFormData({ ...formData, billingFrequency: e.target.value })}
          label="Fizetési gyakoriság"
        >
          <MenuItem value="Monthly">Havi</MenuItem>
          <MenuItem value="Yearly">Éves</MenuItem>
          <MenuItem value="Quarterly">Negyedéves</MenuItem>
        </Select>
      </FormControl>
      <TextField
        fullWidth
        margin="normal"
        label="Következő fizetési dátum"
        type="date"
        value={formData.nextBillingDate?.split('T')[0] || ''}
        onChange={(e) => setFormData({ ...formData, nextBillingDate: e.target.value })}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        fullWidth
        margin="normal"
        label="Megjegyzések"
        value={formData.notes || ''}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        multiline
        rows={3}
      />
    </>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress sx={{ color: '#ff8c42' }} />
        <Typography sx={{ ml: 2 }}>Előfizetések betöltése...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Card sx={{ backgroundColor: 'white' }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2" fontWeight="bold">
              Előfizetések és költségek kezelése
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Frissítés">
                <IconButton onClick={fetchSubscriptions} sx={{ color: '#ff8c42' }}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                sx={{
                  backgroundColor: '#ff8c42',
                  '&:hover': { backgroundColor: '#e67a35' },
                }}
              >
                Új előfizetés
              </Button>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Szolgáltatás</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Szolgáltató</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Havi költség</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fizetési gyakoriság</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Következő fizetés</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Státusz</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Műveletek</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions.map((subscription) => {
                  const daysUntil = getDaysUntilBilling(subscription.nextBillingDate);
                  return (
                    <TableRow key={subscription.id} hover>
                      <TableCell>
                        <Typography variant="body1" fontWeight="medium">
                          {subscription.serviceName}
                        </Typography>
                        {subscription.description && (
                          <Typography variant="caption" color="textSecondary">
                            {subscription.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={subscription.provider} size="small" />
                      </TableCell>
                      <TableCell>${subscription.monthlyPrice.toFixed(2)}</TableCell>
                      <TableCell>{subscription.billingFrequency}</TableCell>
                      <TableCell>
                        {subscription.nextBillingDate ? (
                          <Box>
                            <Typography variant="body2">
                              {new Date(subscription.nextBillingDate).toLocaleDateString('hu-HU')}
                            </Typography>
                            {daysUntil !== null && (
                              <Chip
                                label={`${daysUntil} nap`}
                                size="small"
                                color={getBillingChipColor(daysUntil) as any}
                                sx={{ mt: 0.5 }}
                              />
                            )}
                          </Box>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.isActive ? 'Aktív' : 'Inaktív'}
                          color={subscription.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Tooltip title="Szerkesztés">
                          <IconButton
                            size="small"
                            sx={{ color: '#ff8c42' }}
                            onClick={() => handleEditClick(subscription)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Törlés">
                          <IconButton
                            size="small"
                            sx={{ color: '#f44336' }}
                            onClick={() => handleDeleteClick(subscription)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {subscriptions.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                Nincsenek előfizetések
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Kattints az "Új előfizetés" gombra az első előfizetés hozzáadásához.
              </Typography>
            </Box>
          )}

          {/* Összesítés */}
          {subscriptions.length > 0 && (
            <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Összesítés
              </Typography>
              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Aktív előfizetések
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {subscriptions.filter((s) => s.isActive).length}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Havi összköltség
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    $
                    {subscriptions
                      .filter((s) => s.isActive && s.billingFrequency === 'Monthly')
                      .reduce((sum, s) => sum + s.monthlyPrice, 0)
                      .toFixed(2)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    7 napon belül lejár
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: '#ff9800' }}>
                    {
                      subscriptions.filter((s) => {
                        const days = getDaysUntilBilling(s.nextBillingDate);
                        return days !== null && days >= 0 && days <= 7;
                      }).length
                    }
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Új előfizetés hozzáadása</DialogTitle>
        <DialogContent>{renderDialogForm()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Mégse</Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            sx={{ backgroundColor: '#ff8c42', '&:hover': { backgroundColor: '#e67a35' } }}
          >
            Hozzáadás
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Előfizetés szerkesztése</DialogTitle>
        <DialogContent>{renderDialogForm()}</DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Mégse</Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            sx={{ backgroundColor: '#ff8c42', '&:hover': { backgroundColor: '#e67a35' } }}
          >
            Mentés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Előfizetés törlése</DialogTitle>
        <DialogContent>
          <Typography>
            Biztosan törölni szeretnéd a(z) <strong>{selectedSubscription?.serviceName}</strong> előfizetést?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Mégse</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage(null)}
        message={successMessage}
      />
    </Box>
  );
};

export default SubscriptionsContent;
